import React, { useState, useRef, useEffect } from 'react';
import { validatePassword } from '../../services/authService';
import { Modal } from './Modal';
import { review as reviewMessages } from '../../utils/validationMessages';
import './ElectronicSign.css';

/** Default max chars for Remarks (same order of magnitude as visit vNotes / long text fields). */
const DEFAULT_REMARKS_MAX_LENGTH = 2000;

/**
 * Electronic Sign modal: Password + Reason (vRemarks). Validates both required,
 * POSTs password to Base/ValidatePassword (api/Auth/ValidatePassword).
 * On success: onAuthSuccess(vRemarks) then onSave(); clears password and reason.
 * Validation for missing password/remarks is always shown below the respective fields (inline).
 *
 * @param {boolean} show - Whether modal is visible
 * @param {Function} onClose - Close handler
 * @param {Function} onAuthSuccess - Called with vRemarks on successful auth
 * @param {Function} onSave - Called after onAuthSuccess (e.g. submit form)
 * @param {number} reasonLength - Optional max length for reason; when omitted, {@link DEFAULT_REMARKS_MAX_LENGTH} is used
 * @param {Function} onValidationError - Optional, reserved for future use. Inline validation is always used for required fields.
 */
export function ElectronicSign({
  show,
  onClose,
  onAuthSuccess,
  onSave,
  reasonLength,
  title = 'Authenticate',
  primaryActionLabel = 'Authenticate',
  remarkOnlyMode = false,
  showConfirmText = true,
}) {
  const [password, setPassword] = useState('');
  const [vRemarks, setVRemarks] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [remarksError, setRemarksError] = useState('');
  const [loading, setLoading] = useState(false);
  const postAuthTimeoutRef = useRef(null);

  useEffect(() => () => {
    if (postAuthTimeoutRef.current != null) {
      clearTimeout(postAuthTimeoutRef.current);
      postAuthTimeoutRef.current = null;
    }
  }, []);

  const handleAuthenticate = async () => {
    setPasswordError('');
    setRemarksError('');

    const missingPassword = !remarkOnlyMode && !password.trim();
    const missingRemarks = !vRemarks.trim();
    if (missingPassword || missingRemarks) {
      if (missingPassword) setPasswordError(reviewMessages.passwordRequired);
      if (missingRemarks) setRemarksError(reviewMessages.remarksRequired);
      return;
    }

    setLoading(true);
    try {
      if (remarkOnlyMode) {
        setPassword('');
        setVRemarks('');
        if (onAuthSuccess) onAuthSuccess(vRemarks);
        if (onSave) await onSave(vRemarks);
        onClose();
      } else {
        const result = await validatePassword(password);
        if (result.isSuccess) {
          setPassword('');
          setVRemarks('');
          if (onAuthSuccess) onAuthSuccess(vRemarks);
          if (onSave) {
            if (postAuthTimeoutRef.current != null) clearTimeout(postAuthTimeoutRef.current);
            postAuthTimeoutRef.current = setTimeout(() => {
              postAuthTimeoutRef.current = null;
              onSave();
              onClose();
            }, 200);
          } else onClose();
        } else {
          setPasswordError(result.strMessage || 'Please enter correct password.');
        }
      }
    } catch (err) {
      if (remarkOnlyMode) {
        setRemarksError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to save remark.');
      } else {
        setPasswordError(err.response?.data?.message || err.message || 'Authentication failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (postAuthTimeoutRef.current != null) {
      clearTimeout(postAuthTimeoutRef.current);
      postAuthTimeoutRef.current = null;
    }
    setPasswordError('');
    setRemarksError('');
    setPassword('');
    setVRemarks('');
    onClose();
  };

  if (!show) return null;

  const remarksMaxLength =
    typeof reasonLength === 'number' && reasonLength > 0
      ? reasonLength
      : DEFAULT_REMARKS_MAX_LENGTH;

  const footer = (
    <div className="electronic-sign-footer">
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={handleAuthenticate}
        disabled={loading}
      >
        {loading ? (remarkOnlyMode ? 'Saving…' : 'Authenticating…') : primaryActionLabel}
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClose}>
        Cancel
      </button>
      
    </div>
  );

  return (
    <Modal
      isOpen={show}
      onClose={handleClose}
      title={title}
      size="medium"
      footer={footer}
      showCloseButton={true}
      overlayClassName="electronic-sign-overlay"
    >
      <div className="electronic-sign-body">
        {!remarkOnlyMode ? (
          <div className="form-group">
            <label className="required">Password</label>
            <input
              type="password"
              className={`form-control ${passwordError ? 'is-invalid' : ''}`}
              placeholder="Enter Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError('');
              }}
              autoComplete="off"
            />
            {passwordError && (
              <div className="electronic-sign-field-error text-danger">{passwordError}</div>
            )}
          </div>
        ) : null}
        <div className="form-group">
          <label className="required">Remark</label>
          <textarea
            className={`form-control ${remarksError ? 'is-invalid' : ''}`}
            rows={2}
            placeholder={`Enter Remark (max ${remarksMaxLength} characters)`}
            value={vRemarks}
            onChange={(e) => {
              let next = e.target.value;
              if (next.length > remarksMaxLength) next = next.slice(0, remarksMaxLength);
              setVRemarks(next);
              if (remarksError) setRemarksError('');
            }}
            maxLength={remarksMaxLength}
          />
          {remarksError && (
            <div className="electronic-sign-field-error text-danger">{remarksError}</div>
          )}
        </div>
        {showConfirmText ? (
          <p className="electronic-sign-confirm">
            I hereby confirm signing of this record electronically.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

export default ElectronicSign;
